import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getItemsPaged, deleteItem, updateChecklist, updateItemStatus } from "./PlannerService";
import { exportPlanner } from "../ExportService";
import CommentThread from "../shared/CommentThread";
import "./PlannerList.css";

const PRIORITY_STRIPE = {
  CRITICAL: "#ef4444", HIGH: "#f97316", MEDIUM: "#3b82f6",
  LOW: "#22c55e", SOMEDAY: "#94a3b8",
};
const PRIORITY_LABEL_COLOR = {
  CRITICAL: "#ef4444", HIGH: "#f97316", MEDIUM: "#3b82f6",
  LOW: "#22c55e", SOMEDAY: "#94a3b8",
};

const DATE_SECTIONS = [
  { key: "OVERDUE",   label: "Overdue",   color: "#ef4444" },
  { key: "TODAY",     label: "Today",     color: "#f97316" },
  { key: "TOMORROW",  label: "Tomorrow",  color: "#eab308" },
  { key: "THIS_WEEK", label: "This Week", color: "#3b82f6" },
  { key: "LATER",     label: "Later",     color: "#94a3b8" },
  { key: "NO_DATE",   label: "No Date",   color: "#94a3b8" },
  { key: "DONE",      label: "Done",      color: "#16a34a" },
];

export default function PlannerList() {
  const nav = useNavigate();

  const [items,            setItems]            = useState([]);
  const [priorityFilter,   setPriorityFilter]   = useState("ALL");
  const [dateFilter,       setDateFilter]        = useState("ALL");
  const [categoryFilter,   setCategoryFilter]   = useState("ALL");
  const [projectFilter,    setProjectFilter]    = useState("ALL");
  const [groupByProject,   setGroupByProject]   = useState(false);
  const [searchText,       setSearchText]       = useState("");
  const [page,             setPage]             = useState(0);
  const [hasMore,          setHasMore]          = useState(true);
  const [loading,          setLoading]          = useState(false);
  const [expandedChecklist,setExpandedChecklist]= useState(null);
  const [expandedComments, setExpandedComments] = useState(null);

  const searchRef  = useRef(null);
  const loadMoreRef = useRef(null);

  /* ---- checklist toggle ---- */
  const handleChecklistToggle = async (item, checkId) => {
    const updated = (item.checklistItems || []).map(c =>
      c.id === checkId ? { ...c, done: !c.done } : c
    );
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, checklistItems: updated } : i));
    await updateChecklist(item.id, updated).catch(() => {});
  };

  /* ---- status toggle ---- */
  const handleToggleDone = useCallback(async (item) => {
    const isCompleted = item.status === "DONE" || item.status === "SKIPPED";
    const newStatus = isCompleted ? "ACTIVE" : "DONE";
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: newStatus } : i));
    await updateItemStatus(item.id, newStatus).catch(() => {
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: item.status } : i));
    });
  }, []);

  /* ---- pagination ---- */
  const loadPage = async (nextPage, reset = false) => {
    if (loading) return;
    setLoading(true);
    const data = await getItemsPaged({
      page: nextPage, size: 30,
      priority: priorityFilter, dateFilter,
      category: categoryFilter, project: projectFilter,
      search: searchText,
    });
    const next = Array.isArray(data.items) ? data.items : [];
    setItems(prev => reset ? next : [...prev, ...next]);
    setPage(nextPage);
    setHasMore(Boolean(data.hasMore));
    setLoading(false);
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?\nThis cannot be undone.`)) return;
    await deleteItem(id);
    loadPage(0, true);
  };

  /* ---- effects ---- */
  useEffect(() => { loadPage(0, true); },
    [priorityFilter, dateFilter, categoryFilter, projectFilter, searchText]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting && hasMore && !loading) loadPage(page + 1); }),
      { root: null, rootMargin: "200px", threshold: 0.1 }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [page, hasMore, loading, priorityFilter, dateFilter, categoryFilter, projectFilter, searchText]);

  /* ---- date helpers ---- */
  const today    = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  const nextWeek = new Date(today); nextWeek.setDate(today.getDate() + 7);
  const parseDate  = d => d ? new Date(d + "T00:00:00") : null;
  const getDate    = i => i.dueDate || i.eventDate || i.paymentDate;
  const isCompleted= i => i.status === "DONE" || i.status === "SKIPPED";
  const isOverdue  = i => { const d = parseDate(getDate(i)); return d && d < today && !isCompleted(i); };

  const classifySection = (item) => {
    if (isCompleted(item)) return "DONE";
    const d = parseDate(getDate(item));
    if (!d) return "NO_DATE";
    if (d < today)                      return "OVERDUE";
    if (d.getTime() === today.getTime()) return "TODAY";
    if (d.getTime() === tomorrow.getTime()) return "TOMORROW";
    if (d < nextWeek)                   return "THIS_WEEK";
    return "LATER";
  };

  /* ---- search highlight ---- */
  const highlight = (text) => {
    if (!searchText || typeof text !== "string") return text;
    const parts = text.split(new RegExp(`(${searchText})`, "gi"));
    return parts.map((p, i) =>
      p.toLowerCase() === searchText.toLowerCase()
        ? <mark key={i} className="pl-highlight">{p}</mark>
        : p
    );
  };

  /* ---- row renderer ---- */
  const renderItem = (item) => {
    const cl = item.checklistItems || [];
    const doneCount = cl.filter(c => c.done).length;
    const isExp  = expandedChecklist === item.id;
    const isCommentsExp = expandedComments === item.id;
    const commentCount = item.commentCount || 0;
    const done   = isCompleted(item);
    const overdue= isOverdue(item);
    const stripe = done ? "transparent" : (PRIORITY_STRIPE[item.priority] || "#94a3b8");
    const date   = getDate(item);

    return (
      <div key={item.id}>
        <div
          className={`pl-row${overdue ? " pl-row--overdue" : ""}${done ? " pl-row--done" : ""}`}
          onClick={() => nav(`edit/${item.id}`)}
        >
          <div className="pl-stripe" style={{ background: stripe }} />

          <button
            className={`pl-check${done ? " pl-check--on" : ""}`}
            onClick={e => { e.stopPropagation(); handleToggleDone(item); }}
            title={done ? "Mark active" : "Mark done"}
          >
            {done && "✓"}
          </button>

          <div className="pl-main">
            <div className="pl-title">
              {highlight(item.title)}
              {cl.length > 0 && (
                <button
                  className={`pl-cl-badge${doneCount === cl.length ? " pl-cl-badge--done" : ""}`}
                  onClick={e => { e.stopPropagation(); setExpandedChecklist(isExp ? null : item.id); }}
                >
                  ☑ {doneCount}/{cl.length}
                </button>
              )}
              <button
                className={`pl-cl-badge${isCommentsExp ? " pl-cl-badge--done" : ""}`}
                onClick={e => { e.stopPropagation(); setExpandedComments(isCommentsExp ? null : item.id); }}
              >
                💬 {commentCount}
              </button>
            </div>
            <div className="pl-meta">
              {item.type && (
                <span className="pl-tag pl-tag--type">{item.type}</span>
              )}
              {date && (
                <span className={`pl-date${overdue ? " pl-date--overdue" : ""}`}>{date}</span>
              )}
              {item.category && item.category !== "PERSONAL" && (
                <span className="pl-tag pl-tag--cat">{item.category}</span>
              )}
              {item.projectName && !groupByProject && (
                <span className="pl-tag pl-tag--proj">{item.projectName}</span>
              )}
            </div>
          </div>

          <div className="pl-right">
            <span
              className="pl-pri-label"
              style={{ color: PRIORITY_LABEL_COLOR[item.priority] || "#94a3b8" }}
            >
              {item.priority}
            </span>
          </div>

          <div className="pl-actions" onClick={e => e.stopPropagation()}>
            <button className="pl-act-btn" onClick={() => nav(`edit/${item.id}`)} title="Edit">✏</button>
            <button className="pl-act-btn pl-act-btn--del" onClick={() => handleDelete(item.id, item.title)} title="Delete">✕</button>
          </div>
        </div>

        {isExp && cl.length > 0 && (
          <div className="pl-cl-expand">
            {cl.map(c => (
              <label key={c.id} className="pl-cl-item" onClick={e => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={c.done}
                  onChange={() => handleChecklistToggle(item, c.id)}
                />
                <span className={c.done ? "pl-cl-done" : ""}>{c.text}</span>
              </label>
            ))}
          </div>
        )}

        {isCommentsExp && (
          <div className="pl-cl-expand" onClick={e => e.stopPropagation()}>
            <CommentThread
              compact
              entityType="TASK"
              entityId={item.id}
              onCountChange={count =>
                setItems(prev => prev.map(i => i.id === item.id ? { ...i, commentCount: count } : i))
              }
            />
          </div>
        )}
      </div>
    );
  };

  /* ---- section grouping ---- */
  const showSections = dateFilter === "ALL" && !groupByProject && !searchText;

  const renderSections = () => {
    if (groupByProject) {
      const groups = items.reduce((acc, it) => {
        const k = it.projectName?.trim() || "(No project)";
        acc[k] = acc[k] || [];
        acc[k].push(it);
        return acc;
      }, {});
      return Object.entries(groups)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([proj, projItems]) => (
          <div key={proj} className="pl-section">
            <div className="pl-sec-hdr">
              <span className="pl-sec-dot" style={{ background: "#6d5ef9" }} />
              <span className="pl-sec-label">{proj}</span>
              <span className="pl-sec-count">{projItems.length}</span>
            </div>
            {projItems.map(renderItem)}
          </div>
        ));
    }

    if (showSections) {
      return DATE_SECTIONS.map(sec => {
        const secItems = items.filter(it => classifySection(it) === sec.key);
        if (!secItems.length) return null;
        return (
          <div key={sec.key} className="pl-section">
            <div className="pl-sec-hdr">
              <span className="pl-sec-dot" style={{ background: sec.color }} />
              <span className="pl-sec-label" style={{ color: sec.color }}>{sec.label}</span>
              <span className="pl-sec-count">{secItems.length}</span>
            </div>
            {secItems.map(renderItem)}
          </div>
        );
      });
    }

    return (
      <div className="pl-section">
        {items.map(renderItem)}
      </div>
    );
  };

  /* ---- stats ---- */
  const overdueCount = items.filter(i => isOverdue(i)).length;
  const todayCount   = items.filter(i => { const d = parseDate(getDate(i)); return d && d.getTime() === today.getTime() && !isCompleted(i); }).length;

  return (
    <div className="pl-root">
      {/* ── Header ── */}
      <div className="pl-header">
        <div className="pl-header-left">
          <h2 className="pl-title">Planner</h2>
          <div className="pl-header-stats">
            {overdueCount > 0 && (
              <button
                className="pl-stat-badge pl-stat-badge--overdue"
                onClick={() => setDateFilter("OVERDUE")}
              >
                {overdueCount} overdue
              </button>
            )}
            {todayCount > 0 && dateFilter === "ALL" && (
              <button
                className="pl-stat-badge pl-stat-badge--today"
                onClick={() => setDateFilter("TODAY")}
              >
                {todayCount} today
              </button>
            )}
          </div>
        </div>
        <div className="pl-header-right">
          <button className="pl-btn-primary" onClick={() => nav("new")}>+ New Task</button>
          <button className="pl-btn-ghost" onClick={() => nav("upcoming")}>Upcoming</button>
          <button className="pl-btn-ghost" onClick={() => nav("next-day")}>Next Day</button>
          <button className="pl-btn-ghost" onClick={() => nav("payments")}>Payments</button>
          <button className="pl-btn-ghost" onClick={() => nav("/calendar")}>Calendar</button>
          <button className="pl-btn-ghost" onClick={() => exportPlanner().catch(console.error)}>⬇ Export</button>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="pl-toolbar">
        {/* Search */}
        <div className="pl-search-wrap">
          <span className="pl-search-icon">⌕</span>
          <input
            ref={searchRef}
            type="text"
            placeholder="Search tasks…"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            className="pl-search-input"
          />
          {searchText && (
            <button className="pl-search-clear" onClick={() => { setSearchText(""); searchRef.current?.focus(); }}>✕</button>
          )}
        </div>

        {/* Date filter chips */}
        <div className="pl-date-chips">
          {["ALL", "OVERDUE", "TODAY", "UPCOMING", "NO_DATE", "COMPLETED"].map(f => (
            <button
              key={f}
              className={`pl-chip${dateFilter === f ? " pl-chip--on" : ""}`}
              onClick={() => setDateFilter(f)}
            >
              {f === "ALL" ? "All" : f === "NO_DATE" ? "No Date" : f.charAt(0) + f.slice(1).toLowerCase()}
            </button>
          ))}
        </div>

        {/* Priority select */}
        <select
          value={priorityFilter}
          onChange={e => setPriorityFilter(e.target.value)}
          className="pl-select"
        >
          <option value="ALL">All Priorities</option>
          <option value="CRITICAL">Critical</option>
          <option value="HIGH">High</option>
          <option value="MEDIUM">Medium</option>
          <option value="LOW">Low</option>
          <option value="SOMEDAY">Someday</option>
        </select>

        {/* Category select */}
        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          className="pl-select"
        >
          <option value="ALL">All Categories</option>
          {["WORK","PERSONAL","FINANCE","HEALTH","FAMILY","EDUCATION","BUSINESS",
            "SHOPPING","TRAVEL","BILLING","INVESTMENT","SUBSCRIPTION","INSURANCE",
            "HOME","SOCIAL","GOALS","PROJECT","OTHER"].map(c => (
            <option key={c} value={c}>{c.charAt(0) + c.slice(1).toLowerCase()}</option>
          ))}
        </select>

        {/* Project filter */}
        <input
          type="text"
          placeholder="Project…"
          value={projectFilter === "ALL" ? "" : projectFilter === "NONE" ? "(none)" : projectFilter}
          onChange={e => {
            const v = e.target.value;
            if (!v.trim()) setProjectFilter("ALL");
            else if (v.trim() === "(none)") setProjectFilter("NONE");
            else setProjectFilter(v);
          }}
          className="pl-search-input"
          style={{ maxWidth: 160 }}
        />

        {/* Group toggle */}
        <label className="pl-toggle-label">
          <input
            type="checkbox"
            checked={groupByProject}
            onChange={e => setGroupByProject(e.target.checked)}
          />
          By project
        </label>
      </div>

      {/* ── Item list ── */}
      <div className="pl-list">
        {renderSections()}
      </div>

      <div ref={loadMoreRef} className="pl-load-more">
        {loading ? "Loading…" : hasMore ? "" : items.length > 0 ? `${items.length} items total` : ""}
      </div>
    </div>
  );
}
